import * as API from "../framework/api";
import * as Toast from "../framework/components/toast";

let abortController;

/**
 * Make an element open a lifecycle view with the mouse and the keyboard alike.
 *
 * @param {HTMLElement} el
 * @param {Function} show
 */
export function wireTrigger(el, show) {
  el.setAttribute("role", "button");
  el.setAttribute("tabindex", "0");
  el.addEventListener("click", (event) => {
    // In a grid the pill sits inside a selectable row: the click must not toggle the selection,
    // whose handler would then replace the timeline just shown.
    event.stopPropagation();
    show();
  });
  el.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    show();
  });
}

/**
 * Fetch the server-rendered lifecycle timeline of one document into a slot.
 *
 * The slot fills on the spot with the page's placeholder (real name, grey skeleton steps), the
 * fetched timeline replaces it. A new request cancels the one in flight so responses cannot
 * cross, and repeats land within a frame from the browser cache (the fragments carry
 * stale-while-revalidate).
 *
 * @param {HTMLElement} slotEl
 * @param {Object} opts
 * @param {String} opts.url - The lifecycle fragment URL
 * @param {String} opts.name - The document number shown while loading
 * @param {Function} [opts.onError] - Extra failure handling besides the toast
 */
export async function showLifecycle(slotEl, { url, name, onError }) {
  abortController?.abort();
  const abort = (abortController = new AbortController());

  slotEl.replaceChildren(document.querySelector(".js-invoice-lifecycle-skeleton").content.cloneNode(true));
  slotEl.querySelector(".js-invoice-lifecycle-number").textContent = name ?? "";

  try {
    const response = await API.fetch(url, { signal: abort.signal });
    // fetch follows redirects: a plan/flag gate landing on "/" must not be injected as markup
    if (!response.ok || response.redirected) throw new Error(`lifecycle request failed (${response.status})`);

    const html = await response.text();
    if (abort.signal.aborted) return;

    slotEl.innerHTML = html;
  } catch (_error) {
    if (abort.signal.aborted) return;

    onError?.();
    Toast.show("#invoice-lifecycle-failed");
  }
}
